import { MPCIGridCarousel } from "./typings/iGridCarousel";
import { MPCLocalStorageService } from "../local-storage-service";
const Hammer = require("hammerjs");
const SmartTimeout = require("smart-timeout");
const ArrowRightIcon = require("../../images/arrow-right.svg") as string;

export module MPCGridCarousel {
    import IGridCarousel = MPCIGridCarousel.IGridCarousel;
    import ProductComparisonModelManager = MPCLocalStorageService.ProductComparisonModelManager;

    export class GridCarousel implements IGridCarousel {
        private grid: HTMLElement;
        private navigationContainer: HTMLElement;
        private scrollLeftArrow: HTMLElement;
        private scrollLeftArrowShrinked: HTMLElement;
        private scrollRightArrow: HTMLElement;
        private carouselScrollIndex: number;
        private columnsAmount: number;
        private leftSideColumnIndex: number;
        private carousel: HTMLElement;
        private pagerDotsArray: Array<HTMLElement>;
        private stickyHeaderPagerDotsArray: Array<HTMLElement>;
        private stickyHeader: HTMLElement;
        private scope: HTMLElement;
        private hammerJs: any;
        private hammerManager: HammerManager;
        private productComparisonModelManager: ProductComparisonModelManager;
        private overlay: HTMLElement;

        constructor(scope: HTMLElement, navigationContainer: HTMLElement) {
            this.overlay = scope.querySelector('.mpc-grid-loading-overlay');
            this.grid = scope.querySelector('.grid');
            this.stickyHeader = scope.querySelector('.sticky-header');
            this.scope = scope;
            this.navigationContainer = navigationContainer;
            this.carouselScrollIndex = 1;
            this.leftSideColumnIndex = 1;
            this.columnsAmount = 4;

            if (this.grid.classList.contains("empty-grid"))
                return;

            this.init();
        }

        private init(): void {
            this.productComparisonModelManager = ProductComparisonModelManager.instance;
            this.setCurrentSlideClass();

            this.carousel = this.navigationContainer.querySelector('.grid-carousel-container');
            this.scrollLeftArrow = this.navigationContainer.querySelector('.scroll-left-arrow');
            this.scrollLeftArrowShrinked = this.navigationContainer.querySelector('.scroll-left-arrow-shrinked');
            this.scrollRightArrow = this.navigationContainer.querySelector('.scroll-right-arrow');

            // swiping for grid:
            this.hammerManager = new Hammer.Manager(this.grid, {
                domEvents: true,
                recognizers: [
                    [Hammer.Swipe, { direction: Hammer.DIRECTION_HORIZONTAL, threshold: 5, velocity: 0.1 }]
                ],
                drag_block_vertical: true
            });

            // swiping for sticky header:
            if (this.stickyHeader)
                this.hammerJs = new Hammer(this.stickyHeader);

            const tmpPagerDots: NodeListOf<HTMLElement> = this.carousel.querySelectorAll('.pager-dot');
            this.pagerDotsArray = Array.prototype.slice.call(tmpPagerDots);

            const stickyHeaderContainer: HTMLElement = this.scope.querySelector('.sticky-header-container');

            if (stickyHeaderContainer) {
                const tmpStickyHeaderPagerDots: NodeListOf<HTMLElement> = stickyHeaderContainer.querySelectorAll('.pager-dot');
                this.stickyHeaderPagerDotsArray = Array.prototype.slice.call(tmpStickyHeaderPagerDots);
            }

            this.loadComparisonPageIcons();
            this.registerEvents();
            this.showHideArrows();
            this.updateActivePagerDot();

            if (this.overlay)
                SmartTimeout.set(() => { this.overlay.classList.add('hidde-loading-overlay'); }, 1200);
        }

        private loadComparisonPageIcons(): void {
            this.scrollLeftArrow.innerHTML = ArrowRightIcon;
            this.scrollLeftArrowShrinked.innerHTML = ArrowRightIcon;
            this.scrollRightArrow.innerHTML = ArrowRightIcon;
        }

        private registerEvents(): void {
            this.scrollLeftArrow.addEventListener('click', ()=> { this.handleLeftArrowClick(); });
            this.scrollLeftArrowShrinked.addEventListener('click', ()=> { this.handleLeftArrowClick(); });
            this.scrollRightArrow.addEventListener('click', () => { this.handleRightArrowClick(); });

            this.hammerManager.on('swipe', (event: HammerInput) => {
                let gestureAngle: number = Math.abs(event.angle);

                if (gestureAngle >= 0 && gestureAngle <= 45) {
                    //swipe to right, within a cone of +/-45deg
                    this.handleLeftArrowClick();
                }
                else if (gestureAngle >= 135 && gestureAngle <= 180) {
                    //swipe to left, within a cone of +/-45deg
                    this.handleRightArrowClick();
                }
            });

            if (this.hammerJs) {
                this.hammerJs.on('swipeleft', () => { this.handleRightArrowClick(); });
                this.hammerJs.on('swiperight', () => { this.handleLeftArrowClick(); });
            }
        }

        public shrinkCarousel(shrink: boolean): void {
            if(shrink) {
                this.carousel.classList.add('shrinked');
            }
            else {
                this.carousel.classList.remove('shrinked');
            }

            this.showHideArrows();
        }

        public swipeLeft(): void {
            // if carouse scroll index value is default 1 that it means there is not stored value in localStorage:
            for (let i: number = this.carouselScrollIndex; i >= this.carouselScrollIndex; i--) {
                this.handleLeftArrowClick();
            }
        }

        public restorePreviousSlidePosition(storedCarouselScrollIndex: number): void {
            for (let i: number = 1; i < storedCarouselScrollIndex; i++) {
                this.handleRightArrowClick();
            }
        }

        private handleLeftArrowClick(): void {
            const previousIndex: number = this.carouselScrollIndex;
            this.carouselScrollIndex -= 1; // next carousel scroll index
            this.leftSideColumnIndex -= 1;

            if (this.leftSideColumnIndex < 1)
                this.leftSideColumnIndex = 1;
            
            if(this.carouselScrollIndex < 1)
            {
                // revert carousel scroll index
                this.carouselScrollIndex = 1;
            }
            else {
                this.setCurrentSlideClass(previousIndex);
            }

            this.showHideArrows();
            this.updateActivePagerDot();
            this.storeCarouselScrollIndex();
        }

        private handleRightArrowClick(): void {
            const previousIndex: number = this.carouselScrollIndex;
            this.carouselScrollIndex += 1; // next column on the right
            this.leftSideColumnIndex += 1;

            if(this.leftSideColumnIndex > 3)
                this.leftSideColumnIndex = 3;

            if(this.carouselScrollIndex > 3)
            {
                // revert carousel scroll index
                this.carouselScrollIndex = 3;
            }
            else {
                this.setCurrentSlideClass(previousIndex);
            }

            this.showHideArrows();
            this.updateActivePagerDot();
            this.storeCarouselScrollIndex();
        }

        private setCurrentSlideClass(previousIndex: number = 0): void {
            for (let i: number = 1; i <= this.columnsAmount; i++) {
                const nextVisibleElements: Array<Element> = this.getNextVisibleElements(i);

                for (const nextVisibleElement of nextVisibleElements) {
                    if (!nextVisibleElement.classList.contains('locked-column')) {
                        nextVisibleElement.classList.remove('slide-' + previousIndex);
                        nextVisibleElement.classList.add('slide-' + this.carouselScrollIndex);
                    }
                }
            }
        }
        
        private getNextVisibleElements(gridColumnIndex: number): Array<Element> {
            const selector: string = `[grid-column-index="${gridColumnIndex}"]`;

            const gridElements: NodeListOf<Element> = this.grid.querySelectorAll(selector);
            const gridArray: Array<Element> = Array.prototype.slice.call(gridElements);

            let result: Array<Element> = gridArray;

            if (this.stickyHeader) {
                const headerElements: NodeListOf<Element> = this.stickyHeader.querySelectorAll(selector);
                const headerArray: Array<Element> = Array.prototype.slice.call(headerElements);

                result = result.concat(headerArray);
            }

            return result;
        }
       
        private showHideArrows(): void {
            this.hideScrollArrow(this.scrollLeftArrowShrinked, 1);
            this.hideScrollArrow(this.scrollLeftArrow, 1);
            this.hideScrollArrow(this.scrollRightArrow, 3);
        }

        private hideScrollArrow(arrow: Element, indexToHideFor: number): void {
            const hideScrollArrow: boolean = this.carouselScrollIndex === indexToHideFor;

            if (hideScrollArrow)
                arrow.classList.add('hidden-arrow');
            else
                arrow.classList.remove('hidden-arrow');
        }

        private updateActivePagerDot(): void {
            for(const pagerDot of this.pagerDotsArray) {
                pagerDot.classList.remove('current-dot');
            }

            this.pagerDotsArray[this.carouselScrollIndex - 1].classList.add('current-dot');


            if (this.stickyHeaderPagerDotsArray) {
                for (const pagerDot of this.stickyHeaderPagerDotsArray) {
                    pagerDot.classList.remove('current-dot');
                }

                this.stickyHeaderPagerDotsArray[this.carouselScrollIndex - 1].classList.add('current-dot');
            }
        }

        private storeCarouselScrollIndex(): void {
            this.productComparisonModelManager.storeCarouselScrollIndex(this.carouselScrollIndex);
        }
    }
}